/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryParamsStore } from '../Hooks/Zustand/useQueryParamsStore';
import { useDataAttributesStore } from '../Hooks/Zustand/useDataAttributeStore';

/**
 * Custom Checkout hook
 */
export function useStoreAppFrame() {
  const { setQueryParameters } = useQueryParamsStore();
  const { setDataAttributes } = useDataAttributesStore();
  const [appFrameQueryParametersInitialized, setAppFrameQueryParametersInitialized] = useState(false);
  const [appFrameDataAttributesInitialized, setAppFrameDataAttributesInitialized] = useState(false);
  const queryParamsInitialized = useRef(false);
  const dataAttributesInitialized = useRef(false);

  /** Remove query params from url */
  const removeQueryParamsFromUrl = () => {
    // Iframe Behaviour: hide scrollbar
    if (window.location.href.includes('iframe')) document.querySelector('html').style.overflow = 'hidden';

    if (window.history.replaceState) {
      const urlWithoutQueryParams = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.replaceState({ path: urlWithoutQueryParams }, '', urlWithoutQueryParams);
    }
    setAppFrameQueryParametersInitialized(true);
  };

  /** Get data attributes from checkout form and save to storage */
  useEffect(() => {
    if (!dataAttributesInitialized.current) {
      const checkoutElement = document.querySelector('[data-magicline-checkout]');
      if (checkoutElement) {
        const checkoutAttributes = JSON.parse(checkoutElement.getAttribute('data-magicline-checkout'));
        setDataAttributes(checkoutAttributes);
      }
      dataAttributesInitialized.current = true;
      setAppFrameDataAttributesInitialized(true);
    }
  }, [setDataAttributes]);

  /** Get Query Parameters from URL and save to storage */
  useEffect(() => {
    if (!queryParamsInitialized.current) {
      const searchString = window.location.search.substring(1);
      if (searchString) {
        setQueryParameters(
          JSON.parse(
            `{"${decodeURI(searchString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`,
          ),
        );
      } else {
        setQueryParameters({});
      }
      queryParamsInitialized.current = true;
      removeQueryParamsFromUrl();
    }
  }, [setQueryParameters]);

  return {
    appFrameQueryParametersInitialized,
    appFrameDataAttributesInitialized,
  };
}
